import { ArrowLinkText } from '@/components/common/ArrowLinkText';
import { Button } from '@/components/common/Button';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { SwitchHorizontal01 } from '@/components/common/icons';
import Icon from '@/components/common/icons/Icon';
import {
    Header,
    Paragraph,
    Text,
    TextProps,
} from '@/components/common/Typography';
import { useMutation } from '@apollo/client';
import { Divider } from '@chakra-ui/react';
import { assert } from '@common/errors';
import { CreateGoalAccountOnboardingDocument } from '@common/graphql/generated';
import { iconNameByIraType } from '@common/react/goals';
import { IconName } from '@common/react/icons';
import { useState } from 'react';
import { labelByIraType } from './helpers';
import { useOpenIraFlowContext } from './OpenIraFlow';
import OpenIraFlowStep from './OpenIraFlowStep';
import { useRetirementGoalContext } from './RetirementGoalDetailContainer';

const OpenIraFlowIntroStep = () => {
    const { goal } = useRetirementGoalContext();
    const {
        iraType,
        setStep,
        exitFlow,
        accountOnboardingId,
        setAccountOnboardingId,
    } = useOpenIraFlowContext();
    const [createGoalAccountOnboarding] = useMutation(
        CreateGoalAccountOnboardingDocument
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    return (
        <OpenIraFlowStep
            onBack={exitFlow}
            onNext={() => {
                if (accountOnboardingId) {
                    setStep('BENEFICIARY');
                } else {
                    setIsSubmitting(true);
                    createGoalAccountOnboarding({
                        variables: {
                            goalId: goal.id,
                            info: {
                                infoType: 'RETIREMENT',
                                iraType,
                                lastCompletedRetirementStep: 'INTRO',
                                investmentType: 'DIRECT_INDEXING',
                            },
                        },
                    })
                        .then(({ data }) => {
                            const id =
                                data?.createGoalAccountOnboarding
                                    ?.goalAccountOnboarding?.id;
                            assert(id);
                            setAccountOnboardingId(id);
                            setStep('BENEFICIARY');
                        })
                        .finally(() => setIsSubmitting(false));
                }
            }}
            nextButtonText={`Open a ${labelByIraType[iraType]} IRA`}
            isSubmitting={isSubmitting}
        >
            {iraType === 'TRADITIONAL' && <IntroContentTraditional />}
            {iraType === 'ROTH' && <IntroContentRoth />}
        </OpenIraFlowStep>
    );
};

export default OpenIraFlowIntroStep;

const IntroContentSubheader = (props: TextProps) => (
    <Text fontWeight="semibold" mb={4} {...props} />
);

const IntroContentDivider = () => <Divider my={8} />;

const IntroContentTraditional = () => {
    const { setPosition, goalConfig } = useRetirementGoalContext();
    return (
        <FlexCol>
            <FlexRow justifyContent="space-between" alignItems="center" mb={6}>
                <Header>Open a Plenty Traditional IRA</Header>
                <Button
                    variant="link"
                    onClick={() => setPosition('OPEN_ROTH_IRA')}
                    leftIcon={<SwitchHorizontal01 size="md" />}
                    onClickEvent="Switch Roth IRA Button Clicked"
                >
                    Switch to a Roth IRA
                </Button>
            </FlexRow>
            <Paragraph color="textMuted" mb={16}>
                A traditional IRA help you save more for retirement by delaying
                taxes, so you can put more money to work and your investment
                grows faster. You’ll make tax-deductible contributions (read:
                lowers your taxable income) and your investments grow tax-free.
                You’ll pay taxes when you withdraw your savings.
            </Paragraph>
            <FlexRow gap={20}>
                <FlexCol>
                    <IntroContentSubheader>
                        Who is this best for
                    </IntroContentSubheader>
                    <Paragraph>
                        Best suited for an individual who expects to be in a
                        lower tax bracket when they start withdrawing ie. you
                        earn more now than you will withdraw later.
                    </Paragraph>
                    <IntroContentDivider />
                    <IntroContentSubheader>
                        Contribution eligibility and limits
                    </IntroContentSubheader>
                    <Paragraph>
                        Anybody who earns income is eligible to contribute. The
                        contribution limit for all IRAs is $7,000 for 2024 for
                        those under 50, and $8,000 for those 50 and above.
                    </Paragraph>
                    <IntroContentDivider />
                    <IntroContentSubheader>
                        Deduction eligibility
                    </IntroContentSubheader>
                    <Paragraph>
                        Your eligibility for the tax benefit depends on whether
                        you (or your spouse) have a retirement plan at work, and
                        your income levels.{' '}
                        <ArrowLinkText
                            href="https://www.irs.gov/retirement-plans/ira-deduction-limits"
                            target="_blank"
                            whiteSpace="nowrap"
                            fontSize="md"
                        >
                            Learn more here
                        </ArrowLinkText>
                    </Paragraph>
                    <IntroContentDivider />
                    <IntroContentSubheader>
                        Withdraw penalties
                    </IntroContentSubheader>
                    <Paragraph>
                        If you withdraw the money before you turn 59, you’ll pay
                        a 10% penalty on top of it being taxed as income. In an
                        emergency, you can withdraw anytime.
                    </Paragraph>
                </FlexCol>
                <IntroSidebar
                    title="Plenty Traditional IRA"
                    iconName={iconNameByIraType['TRADITIONAL']}
                    iconColor={goalConfig.color}
                />
            </FlexRow>
        </FlexCol>
    );
};

const IntroContentRoth = () => {
    const { setPosition, goalConfig } = useRetirementGoalContext();
    return (
        <FlexCol>
            <FlexRow justifyContent="space-between" alignItems="center" mb={6}>
                <Header>Open a Plenty Roth IRA</Header>
                <Button
                    variant="link"
                    onClick={() => setPosition('OPEN_TRADITIONAL_IRA')}
                    onClickEvent="Switch Traditional IRA Button Clicked"
                    leftIcon={<SwitchHorizontal01 size="md" />}
                >
                    Switch to a Traditional IRA
                </Button>
            </FlexRow>
            <Paragraph color="textMuted" mb={16}>
                A roth IRA helps you save on taxes down the road, by paying
                taxes upfront. Your investments will grow slower, but you won’t
                need to pay taxes when you withdraw. You’ll make contributions
                after your pay regular taxes and your investments grow tax-free.
            </Paragraph>
            <FlexRow gap={20}>
                <FlexCol>
                    <IntroContentSubheader>
                        Who is this best for
                    </IntroContentSubheader>
                    <Paragraph>
                        Best suited for an individual who expects to be in a
                        higher tax bracket when they start withdrawing ie. you
                        earn less now than you may withdraw later.
                    </Paragraph>
                    <IntroContentDivider />
                    <IntroContentSubheader>
                        Contribution eligibility and limits
                    </IntroContentSubheader>
                    <Paragraph>
                        Eligibility depends on tax filing status and income;
                        single filers are phased out at $138k and married filing
                        jointly at $219k. The contribution limit for all IRAs is
                        $7,000 for 2024 for those under 50, and $8,000 for those
                        50 and above.{' '}
                        <ArrowLinkText
                            href="https://www.irs.gov/retirement-plans/ira-deduction-limits"
                            target="_blank"
                            whiteSpace="nowrap"
                            fontSize="md"
                        >
                            Learn more here
                        </ArrowLinkText>
                    </Paragraph>
                    <IntroContentDivider />
                    <IntroContentSubheader>
                        Withdraw penalties
                    </IntroContentSubheader>
                    <Paragraph>
                        If you withdraw the money before you turn 59, you’ll pay
                        a 10% penalty on top of it being taxed as income. In an
                        emergency, you can withdraw anytime.
                    </Paragraph>
                </FlexCol>
                <IntroSidebar
                    title="Plenty Roth IRA"
                    iconName={iconNameByIraType['ROTH']}
                    iconColor={goalConfig.color}
                />
            </FlexRow>
        </FlexCol>
    );
};

interface IntroSidebarProps {
    title: string;
    iconName: IconName;
    iconColor: string;
}

const IntroSidebar = ({ title, iconName, iconColor }: IntroSidebarProps) => {
    return (
        <FlexCol minW={220}>
            <FlexRow alignItems="center" mb={6}>
                <Icon
                    name={iconName}
                    color={iconColor}
                    size="lg"
                    mt={1}
                    mr={2}
                />
                <Text fontSize="sm" fontWeight="medium">
                    {title}
                </Text>
            </FlexRow>
            <Text fontSize="sm" fontWeight="medium" mb={3}>
                Fees
            </Text>
            <Paragraph fontSize="sm" color="textMuted" mb={3}>
                0.20% investment fee
            </Paragraph>
            <Paragraph fontSize="sm" color="textMuted" mb={6}>
                0.06%-0.13% expense ratios depending on selected portfolio.
            </Paragraph>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
                Investments
            </Text>
            <Paragraph fontSize="sm" color="textMuted" mb={6}>
                Direct indexed stock+bond portfolio
            </Paragraph>
            <Text fontSize="sm" mb={2}>
                Custodian
            </Text>
            <Paragraph fontSize="sm" color="textMuted" mb={6}>
                BNY Mellon | Pershing, the world’s largest securities servicing
                company
            </Paragraph>
            <Text fontSize="sm" mb={2}>
                Insurance
            </Text>
            <Paragraph fontSize="sm" color="textMuted">
                $500k SIPC Insurance
            </Paragraph>
        </FlexCol>
    );
};
